"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useGeocodingSearch = exports.getGeocodingSearchResult = void 0;
var _auth = require("@ori/auth");
var _environment = require("@ori/environment");
var _fetching = require("@ori/fetching");
var _i18n = require("@ori/i18n");
var _swrConfig = require("../constants/swrConfig");
var _SearchConfigurationProvider = require("../containers/SearchConfigurationProvider");
var _models = require("../models");
var _utils = require("../utils");
const getCountryNameByTenant = tenant => new Intl.DisplayNames(['en'], {
  type: 'region'
}).of(tenant.toUpperCase());
const mapModules = {
  [_models.MapProviderType.Bing]: async ({
    tenant,
    credentials,
    searchTerm,
    ...options
  }) => {
    const searchParams = {
      // TODO: culture: localeString, // 'cs', 'en-GB', looks like only selected subset of standard locales is supported
      key: credentials,
      maxResults: '1',
      query: `${searchTerm},${getCountryNameByTenant(tenant)}`,
      incl: 'ciso2' // include two-letter ISO country code in results
    };
    const data = await (0, _utils.catchPromiseError)((0, _utils.getUrl)({
      ...options,
      tenant,
      query: `?${new URLSearchParams(searchParams)}`,
      basePath: '/REST/v1/Locations',
      host: 'https://dev.virtualearth.net',
      isAddingSpecialHeadersForbidden: true // Avoid CORS problems
    }), 'Error in bingGeocodingSearch');

    // Results are filtered to get items relevant to tenant but not to have country only
    return (data?.resourceSets[0]?.resources ?? []).filter(item => item.address.countryRegionIso2 === tenant.toUpperCase() && item.entityType !== 'CountryRegion').map(item => ({
      label: item.address.formattedAddress ?? '',
      latitude: item.point.coordinates[0],
      longitude: item.point.coordinates[1]
    }))[0];
  },
  [_models.MapProviderType.Google]: async ({
    tenant,
    credentials,
    searchTerm,
    ...options
  }) => {
    const searchParams = {
      key: credentials,
      address: searchTerm
      // TODO: language: localeString,
      // TODO: maxResults: '1', // doesn't seem to be supported
    };
    const data = await (0, _utils.catchPromiseError)((0, _utils.getUrl)({
      ...options,
      tenant,
      query: `?${new URLSearchParams(searchParams)}`,
      basePath: '/maps/api/geocode/json',
      host: 'https://maps.googleapis.com',
      isAddingSpecialHeadersForbidden: true // Avoid CORS problems
    }), 'Error in googleGeocodingSearch');

    // Results are filtered to get items relevant to tenant but not to have country only
    return (data?.results ?? []).map(item => ({
      label: item.formatted_address,
      // Incorrectly typed in google maps types needs manual casting
      latitude: item.geometry.location.lat,
      longitude: item.geometry.location.lng
    }))[0];
  },
  [_models.MapProviderType.Yandex]: async ({
    tenant,
    credentials,
    searchTerm,
    ...options
  }) => {
    const searchParams = {
      // TODO: lang: localeString, // 'cs', 'en-GB', looks like only selected subset of standard locales is supported
      apikey: credentials,
      results: '1',
      geocode: searchTerm,
      format: 'json'
    };
    const data = await (0, _utils.catchPromiseError)((0, _utils.getUrl)({
      ...options,
      tenant,
      query: `?${new URLSearchParams(searchParams)}`,
      basePath: '1.x',
      host: 'https://geocode-maps.yandex.ru'
    }), 'Error in yandexGeocodingSearch');
    return (data?.response.GeoObjectCollection.featureMember ?? []).map(({
      GeoObject
    }) => {
      const [longitude = 0, latitude = 0] = GeoObject.Point.pos.split(' ').map(Number);
      return {
        label: GeoObject.metaDataProperty.GeocoderMetaData.Address.formatted,
        latitude,
        longitude
      };
    })[0];
  }
};
const getGeocodingSearchResult = async options => !options.searchTerm ? undefined : mapModules[options.provider](options);

/**
 * @obsolete Can be dropped later when flat selection UI is used in all markets
 * and/or the merged map & list UI switch is enabled everywhere.
 */
exports.getGeocodingSearchResult = getGeocodingSearchResult;
const useGeocodingSearch = ({
  searchTerm,
  isActive
}) => {
  const {
    formattedToken
  } = (0, _auth.useToken)();
  const {
    tenant
  } = (0, _environment.useEnvironment)();
  const {
    language
  } = (0, _i18n.useLanguage)();
  const {
    credentials,
    mapProvider
  } = (0, _SearchConfigurationProvider.useSearchConfiguration)();
  return (0, _fetching.useSWR)(() => {
    if (!isActive || !searchTerm) {
      return null;
    }
    return {
      credentials,
      searchTerm,
      currentLanguage: language,
      formattedToken,
      tenant
    };
  }, async () => getGeocodingSearchResult({
    provider: mapProvider,
    credentials,
    searchTerm,
    currentLanguage: language,
    formattedToken,
    tenant
  }), _swrConfig.errorHandlingSwrOptions);
};
exports.useGeocodingSearch = useGeocodingSearch;